/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchBattingAnalysis, fetchBowlingAnalysis, fetchFieldingAnalysis } from "./profileTabsApi";
import { BarRow, Card, Chips, EmptyState, QueryState, Segmented, SimpleTable, StatGrid, num } from "./ProfileCommon";

type Kind = "batting" | "bowling" | "fielding";
const fmtDate = (d: unknown) => (d ? new Date(d as any).toISOString().slice(0, 10) : "");
const oversFromBalls = (b?: number | null) => (b == null ? "" : ` (${Math.floor(b / 6)}.${b % 6} ov)`);
const nz = <T,>(a?: T[] | null) => (Array.isArray(a) ? a : []);

const Batting = ({ d }: { d: any }) => {
  const [bowlerFilter, setBowlerFilter] = useState("ALL");
  const [splitView, setSplitView] = useState<"inn" | "res">("inn");
  const rec = d?.playerBattingStats;
  const order = nz<any>(d?.battingOrderStats).slice().sort((a, b) => (a.battingOrder ?? 99) - (b.battingOrder ?? 99));
  const bowlerTypes = nz<any>(d?.bowlerTypeBattingStats);
  const shots = nz<any>(d?.shotAreaBattingStats);
  const innings = nz<any>(d?.inningBattingStats).slice().sort((a, b) => (a.inningNumber ?? 0) - (b.inningNumber ?? 0));
  const results = nz<any>(d?.resultBasedBattingStats);
  const recent = nz<any>(d?.recentMatches);
  const tournaments = nz<any>(d?.tournamentBattingStats);

  const shortCodes = useMemo(() => Array.from(new Set(shots.map((s) => s.bowlerTypeShortCode).filter(Boolean))).sort() as string[], [shots]);
  const sectors = useMemo(() => {
    const m = new Map<string, any>();
    shots
      .filter((s) => bowlerFilter === "ALL" || s.bowlerTypeShortCode === bowlerFilter)
      .forEach((s) => {
        const k = s.shotDirection ?? "Unknown";
        const c = m.get(k) ?? { runs: 0, ones: 0, twos: 0, threes: 0, fours: 0, sixes: 0 };
        c.runs += s.runsScored ?? 0;
        c.ones += s.onesCount ?? 0; c.twos += s.twosCount ?? 0; c.threes += s.threesCount ?? 0;
        c.fours += s.foursCount ?? 0; c.sixes += s.sixesCount ?? 0;
        m.set(k, c);
      });
    return Array.from(m.entries()).map(([name, v]) => ({ name, ...v })).sort((a, b) => b.runs - a.runs);
  }, [shots, bowlerFilter]);
  const totalRuns = sectors.reduce((a, s) => a + s.runs, 0);

  const maxOrder = Math.max(0, ...order.map((o) => o.runsScored ?? 0));
  const maxInn = Math.max(0, ...innings.map((o) => o.runsScored ?? 0));
  const maxRes = Math.max(0, ...results.map((o) => o.runsScored ?? 0));
  const resLabel = (r?: string) => (/^(WON|WIN)$/i.test(r ?? "") ? "Won" : /^(LOST|LOSS)$/i.test(r ?? "") ? "Lost" : "Tie");

  if (!rec && !order.length && !bowlerTypes.length && !shots.length && !innings.length && !recent.length && !tournaments.length)
    return <EmptyState title="No batting analysis yet" hint="Batting insights will appear here once available." />;

  return (
    <div className="pt-grid">
      {rec && (
        <Card title="Batting Record" subtitle="All tournaments combined" wide>
          <StatGrid items={[
            { label: "Runs", value: num(rec.runsScored) }, { label: "Average", value: num(rec.battingAverage, 2) },
            { label: "SR", value: num(rec.strikeRate, 2) }, { label: "Best", value: num(rec.highestScore) },
            { label: "Matches", value: num(rec.matchesPlayed) }, { label: "Innings", value: num(rec.inningsPlayed) },
            { label: "Balls", value: num(rec.ballsFaced) }, { label: "Not outs", value: num(rec.notOuts) },
            { label: "50s", value: num(rec.fifties) }, { label: "100s", value: num(rec.hundreds) },
          ]} cols={5} />
        </Card>
      )}
      {order.length > 0 && (
        <Card title="Batting Order" subtitle={order.length === 1 ? "Every innings at the same position" : `Across ${order.length} positions in the order`}>
          {order.map((o) => (
            <BarRow key={o.battingOrder ?? "u"} title={o.battingOrder != null ? `No. ${o.battingOrder}` : "Unknown"} right={`${o.inningsPlayed ?? 0} innings`}
              value={o.runsScored ?? 0} max={maxOrder} barLabel={`${o.runsScored ?? 0} runs`}
              chips={[{ label: "Avg", value: num(o.battingAverage, 2) }, { label: "SR", value: num(o.strikeRate, 2) }, { label: "Not outs", value: num(o.notOuts) }]} />
          ))}
        </Card>
      )}
      {bowlerTypes.length > 0 && (
        <Card title="Comfort level" subtitle="Against each bowling type faced">
          {bowlerTypes.map((b, i) => (
            <div className="pt-bar-row" key={i}>
              <div className="pt-bar-top"><span className="pt-bar-title">{b.bowlerType ?? "Unknown"}</span>{b.bowlerTypeShortCode && <span className="pt-pill">{b.bowlerTypeShortCode}</span>}</div>
              <Chips items={[{ label: "SR", value: num(b.strikeRate, 2) }, { label: "Runs", value: num(b.runsScored) }, { label: "Balls", value: `${num(b.ballsFaced)}${oversFromBalls(b.ballsFaced)}` }, { label: "Out", value: num(b.notOuts) }, { label: "Avg", value: num(b.battingAverage, 2) }]} />
            </div>
          ))}
        </Card>
      )}
      {shots.length > 0 && (
        <Card title="Shot analysis" subtitle="Where the runs come from" wide action={
          <select className="pt-select" value={bowlerFilter} onChange={(e) => setBowlerFilter(e.target.value)} aria-label="Bowling type faced">
            <option value="ALL">All bowling</option>
            {shortCodes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>}>
          {sectors.length === 0 ? <p className="pt-muted">No shots recorded for this filter.</p> : (
            <>
              <StatGrid cols={4} items={[
                { label: "Runs shown", value: String(totalRuns) },
                { label: "Scoring shots", value: String(sectors.reduce((a, s) => a + s.ones + s.twos + s.threes + s.fours + s.sixes, 0)) },
                { label: "Fours", value: String(sectors.reduce((a, s) => a + s.fours, 0)) },
                { label: "Sixes", value: String(sectors.reduce((a, s) => a + s.sixes, 0)) },
              ]} />
              <h4 className="pt-sub">Runs by sector</h4>
              <div className="pt-two-col">
                {sectors.map((s) => (
                  <BarRow key={s.name} title={s.name} right={totalRuns ? `${Math.round((s.runs / totalRuns) * 100)}%` : ""} value={s.runs} max={sectors[0].runs} barLabel={`${s.runs} runs`}
                    chips={[{ label: "4s", value: String(s.fours) }, { label: "6s", value: String(s.sixes) }]} />
                ))}
              </div>
            </>
          )}
        </Card>
      )}
      {(innings.length > 0 || results.length > 0) && (
        <Card title="Splits" subtitle="Innings wise and result wise" action={<Segmented value={splitView} onChange={setSplitView} options={[{ key: "inn", label: "Innings" }, { key: "res", label: "Results" }]} />}>
          {splitView === "inn" ? (innings.length ? innings.map((s, i) => {
            const dism = (s.inningsPlayed ?? 0) - (s.notOuts ?? 0);
            const fiftyPlus = (s.fifties ?? 0) + (s.hundreds ?? 0);
            return (
              <BarRow key={i} title={s.inningNumber === 1 ? "Batting 1st" : s.inningNumber === 2 ? "Batting 2nd" : s.inningNumber ? `Innings ${s.inningNumber}` : "Unknown innings"} right={`${s.inningsPlayed ?? 0} innings batted`}
                value={s.runsScored ?? 0} max={maxInn} barLabel={`${s.runsScored ?? 0} runs`}
                chips={[{ label: "Avg", value: dism > 0 ? ((s.runsScored ?? 0) / dism).toFixed(1) : dash1 }, { label: "SR", value: num(s.strikeRate, 1) }, { label: "50s", value: num(s.fifties) }, { label: "100s", value: num(s.hundreds) },
                  { label: "50+ score", value: fiftyPlus ? `every ${((s.inningsPlayed ?? 0) / fiftyPlus).toFixed(1)} inns` : "None yet" }]} />
            );
          }) : <p className="pt-muted">No innings splits yet</p>) : results.length ? (
            <>
              {results.map((s, i) => (
                <BarRow key={i} title={resLabel(s.matchResult)} right={`${s.ballsFaced ?? 0} balls faced`} value={s.runsScored ?? 0} max={maxRes} barLabel={`${s.runsScored ?? 0} runs`}
                  chips={[{ label: "Avg", value: num(s.battingAverage, 1) }, { label: "SR", value: num(s.strikeRate, 1) }, { label: "Not outs", value: num(s.notOuts) }]} />
              ))}
              <p className="pt-muted pt-note">Only matches that ended in a result, so these do not add up to career figures.</p>
            </>
          ) : <p className="pt-muted">No completed matches yet</p>}
        </Card>
      )}
      {recent.length > 0 && (
        <Card title="Innings log" subtitle={`${rec?.matchesPlayed != null ? `${rec.matchesPlayed} matches · ` : ""}Last ${recent.length} matches`} wide>
          <SimpleTable cols={["Match", "Runs", "Balls", "SR", "Tournament", "Date"]}
            rows={recent.map((m) => [`${m.playerTeamName ?? ""} vs ${m.opponentTeamName ?? ""}`, num(m.runsScored), num(m.ballsFaced), num(m.strikeRate, 2), m.tournamentName ?? "", fmtDate(m.matchDate)])} />
        </Card>
      )}
      {tournaments.length > 0 && (
        <Card title="Recent Tournaments" subtitle={`Latest ${tournaments.length}, newest first`} wide>
          <SimpleTable cols={["Tournament", "M", "INN", "RUNS", "HS", "AVG", "SR", "50S", "100S"]}
            rows={tournaments.map((t) => [t.tournament?.tournamentName ?? dash1, num(t.matchesPlayed), num(t.inningsPlayed), num(t.runsScored), num(t.highestScore), num(t.battingAverage, 2), num(t.strikeRate, 2), num(t.fifties), num(t.hundreds)])} />
        </Card>
      )}
    </div>
  );
};
const dash1 = "—";

const Bowling = ({ d }: { d: any }) => {
  const [splitView, setSplitView] = useState<"inn" | "res">("inn");
  if (!d) return <EmptyState title="No bowling analysis yet" hint="Bowling insights will appear here once available." />;
  const r = d.record ?? {};
  const cl = d.comfortLevel ?? {};
  const sectors = nz<any>(d.wagonWheel?.sectors).slice().sort((a, b) => (b.runsConceded ?? 0) - (a.runsConceded ?? 0));
  const sum = d.wagonWheel?.summary ?? {};
  const sp = d.splits ?? {};
  const splitSections: [string, any][] = splitView === "inn" ? [["Bowled 1st Innings", sp.bowledFirstInnings], ["Bowled 2nd Innings", sp.bowledSecondInnings]] : [["Won", sp.matchesWon], ["Lost", sp.matchesLost]];
  const entries = nz<any>(d.log?.entries);
  const rows = nz<any>(d.tournamentRows);
  const hand = (label: string, h: any) => h && (
    <div className="pt-bar-row" key={label}>
      <div className="pt-bar-top"><span className="pt-bar-title">{label}</span></div>
      <Chips items={[{ label: "Econ", value: num(h.economy, 2) }, { label: "Wkts", value: num(h.wickets) }, { label: "Balls", value: `${num(h.ballsBowled)}${h.oversDisplay ? ` (${h.oversDisplay} ov)` : ""}` }, { label: "Runs", value: num(h.runsConceded) }, { label: "Avg", value: num(h.average, 1) }]} />
    </div>
  );
  return (
    <div className="pt-grid">
      <Card title="Bowling record" subtitle={r.tournamentName ?? "All tournaments combined"} wide>
        <StatGrid cols={4} items={[
          { label: "Wickets", value: num(r.wickets) }, { label: "Average", value: num(r.average, 1) }, { label: "Econ", value: num(r.economy, 2) }, { label: "Best", value: r.bestBowlingFigures ?? dash1 },
          { label: "Matches", value: num(r.matches) }, { label: "Innings", value: num(r.innings) }, { label: "Overs", value: r.overs ?? dash1 }, { label: "Maidens", value: num(r.maidens) },
          { label: "Runs", value: num(r.runsConceded) }, { label: "Balls/Wkt", value: num(r.ballsPerWicket, 1) }, { label: "3+ Wkts", value: num(r.threePlusWicketHauls) }, { label: "Wkts/Match", value: num(r.wicketsPerMatch, 1) },
        ]} />
      </Card>
      <Card title="Comfort level" subtitle="Against right and left hand batters">
        {hand("Right-hand batters", cl.rightHandBatters)}{hand("Left-hand batters", cl.leftHandBatters)}
        {!cl.rightHandBatters && !cl.leftHandBatters && <p className="pt-muted">No data yet</p>}
      </Card>
      <Card title="Where he is scored off" subtitle="Where batters hit the runs conceded">
        <StatGrid cols={4} items={[{ label: "Runs conceded", value: num(sum.runsConceded) }, { label: "Scoring shots", value: num(sum.scoringShots) }, { label: "Fours", value: num(sum.fours) }, { label: "Sixes", value: num(sum.sixes) }]} />
        {sectors.map((s) => <BarRow key={s.sector} title={s.label ?? s.sector} right={s.percentage != null ? `${Math.round(s.percentage)}%` : ""} value={s.runsConceded ?? 0} max={sectors[0]?.runsConceded ?? 0} barLabel={`${s.runsConceded ?? 0} runs`} />)}
      </Card>
      <Card title="Splits" subtitle="Innings wise and result wise" action={<Segmented value={splitView} onChange={setSplitView} options={[{ key: "inn", label: "Innings" }, { key: "res", label: "Results" }]} />}>
        {splitSections.map(([label, s]) => s ? (
          <BarRow key={label} title={label} right={`${s.inningsBowled ?? 0} innings bowled`} value={s.wicketShare ?? 0} max={1} barLabel={`${s.wickets ?? 0} wickets`}
            chips={[{ label: "Overs", value: String(s.overs ?? dash1) }, { label: "Econ", value: num(s.economy, 2) }, { label: "Avg", value: num(s.average, 1) }, { label: "Best", value: s.bestBowlingFigures ?? dash1 }]} />
        ) : null)}
      </Card>
      <Card title="Bowling log" subtitle={`${d.log?.totalMatches ?? entries.length} matches · last ${entries.length}`} wide>
        {entries.length ? <SimpleTable cols={["Figures", "Overs", "Opponent", "Result", "Ground", "Maidens", "Econ", "Date"]}
          rows={entries.map((e) => [e.figures ?? dash1, num(e.overs), e.opponentTeamName ?? "", e.resultForBowler ?? "", e.groundName ?? "", num(e.maidens), num(e.economy, 2), fmtDate(e.matchDate)])} /> : <p className="pt-muted">No bowling performances yet</p>}
      </Card>
      <Card title="All Tournament" subtitle={`Last ${rows.length} tournaments`} wide>
        {rows.length ? <SimpleTable cols={["Tournament", "M", "Inn", "Overs", "Wkts", "Econ", "BBI", "5W", "Avg"]}
          rows={rows.map((t) => [t.tournamentName ?? dash1, num(t.matches), num(t.innings), String(t.overs ?? dash1), num(t.wickets), num(t.economy, 2), t.bestBowlingFigures ?? dash1, num(t.fiveWicketHauls), num(t.average, 1)])} /> : <p className="pt-muted">No tournament data yet</p>}
      </Card>
    </div>
  );
};

const Fielding = ({ d }: { d: any }) => {
  const [splitView, setSplitView] = useState<"inn" | "res">("inn");
  if (!d) return <EmptyState title="No fielding analysis yet" hint="Fielding insights will appear here once available." />;
  const r = d.record ?? {};
  const wk = d.wicketKeepingRecord;
  const br = nz<any>(d.dismissalBreakdown);
  const sp = d.splits ?? {};
  const sections: [string, any][] = splitView === "inn" ? [["Fielding 1st innings", sp.bowledFirstInnings], ["Fielding 2nd innings", sp.bowledSecondInnings]] : [["Won", sp.matchesWon], ["Lost", sp.matchesLost]];
  const entries = nz<any>(d.log?.entries);
  const rows = nz<any>(d.tournamentRows);
  return (
    <div className="pt-grid">
      <Card title="Fielding" subtitle="All tournaments" wide>
        <StatGrid cols={5} items={[
          { label: "Dismissals", value: num(r.dismissals) }, { label: "Catches", value: num(r.catches) }, { label: "Run-outs", value: num(r.runOuts) }, { label: "Best", value: String(r.bestFiguresInMatch ?? dash1) }, { label: "Matches", value: num(r.matches) },
          { label: "In the field", value: num(r.inningsFielded) }, { label: "Ct as keeper", value: num(r.catchesAsKeeper) }, { label: "Dis / match", value: num(r.dismissalsPerMatch, 1) }, { label: "Stumpings", value: num(r.stumpings) }, { label: "Innings kept", value: num(r.inningsKept) },
        ]} />
      </Card>
      {wk && (
        <Card title="Wicket keeping" subtitle={`${wk.inningsKept ?? 0} innings behind the stumps`}>
          <StatGrid cols={4} items={[
            { label: "Dismissals", value: num(wk.dismissals) }, { label: "Caught", value: num(wk.caught) }, { label: "Stumped", value: num(wk.stumped) }, { label: "Byes / inn", value: num(wk.byesPerInnings, 1) },
            { label: "Innings kept", value: num(wk.inningsKept) }, { label: "Byes conceded", value: num(wk.byesConceded) }, { label: "Dis / inn", value: num(wk.dismissalsPerInnings, 1) }, { label: "Stumping share", value: wk.stumpingShare != null ? `${(wk.stumpingShare * 100).toFixed(1)}%` : dash1 },
          ]} />
        </Card>
      )}
      {br.length > 0 && (
        <Card title="How his dismissals come" subtitle="Every dismissal he has been involved in">
          {br.map((b) => (
            <div className="pt-bar-row" key={b.tag}>
              <div className="pt-bar-top"><span className="pt-bar-title"><span className="pt-pill">{b.tag}</span> {b.label}</span></div>
              <Chips items={[{ label: "Count", value: num(b.count) }, { label: "Share", value: b.sharePercent != null ? `${Number(b.sharePercent).toFixed(1)}%` : dash1 }]} />
            </div>
          ))}
        </Card>
      )}
      <Card title="Splits" subtitle="Innings wise and result wise" action={<Segmented value={splitView} onChange={setSplitView} options={[{ key: "inn", label: "Innings" }, { key: "res", label: "Results" }]} />}>
        {sections.map(([label, s]) => s ? (
          <BarRow key={label} title={label} right={`${s.inningsFielded ?? 0} innings fielded`} value={s.dismissalShare ?? 0} max={1} barLabel={`${s.dismissals ?? 0} dismissal${s.dismissals === 1 ? "" : "s"}`}
            chips={[{ label: "Catches", value: num(s.catches) }, { label: "Stumpings", value: num(s.stumpings) }, { label: "Run-outs", value: num(s.runOuts) }]} />
        ) : null)}
      </Card>
      <Card title="Fielding log" subtitle={`${d.log?.totalMatches ?? entries.length} matches · last ${entries.length}`} wide>
        {entries.length ? <SimpleTable cols={["Dis", "Opponent", "Ground", "Date", "Catches", "Stumpings", "Run-outs", "Result"]}
          rows={entries.map((e) => [num(e.dismissals), e.opponentTeamName ?? "", e.groundName ?? "", fmtDate(e.matchDate), num(e.catches), num(e.stumpings), num(e.runOuts), e.resultForFielder === "NONE" ? dash1 : e.resultForFielder ?? dash1])} /> : <p className="pt-muted">No fielding performances yet</p>}
      </Card>
      {rows.length > 0 && (
        <Card title="All Tournament" subtitle={`Last ${rows.length} tournaments`} wide>
          <SimpleTable cols={["Tournament", "M", "INN", "DIS"]} rows={rows.map((t) => [t.tournamentName ?? dash1, num(t.matches), num(t.innings), num(t.dismissals)])} />
        </Card>
      )}
    </div>
  );
};

const ProfileAnalysisTab = ({ playerId }: { playerId: string }) => {
  const [kind, setKind] = useState<Kind>("batting");
  const fn = { batting: fetchBattingAnalysis, bowling: fetchBowlingAnalysis, fielding: fetchFieldingAnalysis }[kind];
  const q = useQuery({ queryKey: ["profileAnalysis", kind, playerId], queryFn: () => fn(playerId), enabled: !!playerId });
  return (
    <div className="pt-root">
      <Segmented value={kind} onChange={setKind} options={[{ key: "batting", label: "Batting" }, { key: "bowling", label: "Bowling" }, { key: "fielding", label: "Fielding" }]} />
      <QueryState loading={q.isLoading} error={q.isError} onRetry={() => q.refetch()}>
        {kind === "batting" ? <Batting d={q.data} /> : kind === "bowling" ? <Bowling d={q.data} /> : <Fielding d={q.data} />}
      </QueryState>
    </div>
  );
};

export default ProfileAnalysisTab;
